import {Controller} from '@hotwired/stimulus';
import {addPopStateGuard, removePopStateGuard} from '@/lib/popstate_guard';

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export default class extends Controller {

  static targets = [
    "form",
  ];

  static values = {
    message: String,
    initialState: String,
  };

  declare formTarget: HTMLFormElement;
  declare messageValue: string;
  declare initialStateValue: string;
  declare hasInitialStateValue: boolean;

  private path = "";
  private ignoreNextPopState = false;

  connect() {
    // Stored as a data attribute so it survives in Turbo's snapshot cache: when going Back to a
    // cached page containing unsaved edits, those edits must still count as dirty. A fresh server
    // render (e.g. after a successful save) has no attribute and starts a new baseline.
    if (!this.hasInitialStateValue) {
      this.initialStateValue = this.serialize();
      // Vue widgets mount on turbo:load, after connect(), and may render named fields of their own
      document.addEventListener("turbo:load", this.rebaseline, { once: true });
      this.formTarget.addEventListener("input", this.cancelRebaseline, { once: true });
      this.formTarget.addEventListener("change", this.cancelRebaseline, { once: true });
    }
    this.path = this.currentPath();

    this.addLeaveListeners();
    this.formTarget.addEventListener("submit", this.handleSubmit);
    this.formTarget.addEventListener("turbo:submit-end", this.handleSubmitEnd);
  }

  disconnect() {
    this.cancelRebaseline();
    this.removeLeaveListeners();
    this.formTarget.removeEventListener("submit", this.handleSubmit);
    this.formTarget.removeEventListener("turbo:submit-end", this.handleSubmitEnd);
  }

  rebaseline = () => {
    this.initialStateValue = this.serialize();
    this.cancelRebaseline();
  };

  // Once the user has edited the form, its current state is no longer a valid baseline
  cancelRebaseline = () => {
    document.removeEventListener("turbo:load", this.rebaseline);
    this.formTarget.removeEventListener("input", this.cancelRebaseline);
    this.formTarget.removeEventListener("change", this.cancelRebaseline);
  };

  handleSubmit = () => {
    this.removeLeaveListeners();
  };

  // A failed Turbo submission (network error, or an error response rendered in place) can leave
  // this form mounted, so connect() won't run again to restore the guards.
  handleSubmitEnd = (event: Event) => {
    const { success } = (event as CustomEvent<{ success: boolean }>).detail;
    if (!success && this.element.isConnected) this.addLeaveListeners();
  };

  addLeaveListeners() {
    window.addEventListener("beforeunload", this.beforeUnload);
    // Turbo Drive navigations don't trigger beforeunload
    document.addEventListener("turbo:before-visit", this.beforeVisit);
    // Runs before Turbo's own popstate listener, so we can stop its restoration visit
    addPopStateGuard(this.popState);
  }

  removeLeaveListeners() {
    window.removeEventListener("beforeunload", this.beforeUnload);
    document.removeEventListener("turbo:before-visit", this.beforeVisit);
    removePopStateGuard(this.popState);
  }

  isDirty() {
    return this.serialize() !== this.initialStateValue;
  }

  // Compared on demand rather than tracked via input events, so changes made by widgets that
  // set values programmatically (rich text editors, select libraries) are caught too.
  // Disabled fields are included, as some forms toggle them without the user changing anything.
  serialize() {
    const fields = Array.from(this.formTarget.elements).filter(
      (el): el is FieldElement =>
        (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) && !!el.name
    );

    return JSON.stringify(fields.map((el) => {
      if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) return [el.name, el.value, el.checked];
      if (el instanceof HTMLSelectElement) return [el.name, Array.from(el.selectedOptions, (option) => option.value)];
      return [el.name, el.value];
    }));
  }

  currentPath() {
    return window.location.pathname + window.location.search;
  }

  beforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.isDirty()) return;

    event.preventDefault();
    event.returnValue = true;
  };

  beforeVisit = (event: Event) => {
    if (!this.isDirty()) return;

    if (!window.confirm(this.messageValue)) event.preventDefault();
  };

  // Back/forward within Turbo is a same-document history change, so neither beforeunload nor
  // turbo:before-visit fires. popstate can't be cancelled either: the URL has already changed, so
  // on cancel we stop Turbo's restoration visit and step history back to the form's entry.
  // Both that popstate and the compensating one are hidden from Turbo, so its position is untouched
  // and still matches the entry we return to.
  popState = (event: PopStateEvent) => {
    if (this.ignoreNextPopState) {
      this.ignoreNextPopState = false;
      event.stopImmediatePropagation();
      return;
    }

    // In-page history entries (e.g. anchors) don't leave the form
    if (this.currentPath() === this.path) return;

    const index = event.state?.turbo?.restorationIndex;
    // The entry being left. Turbo indexes every entry it sees, including ones pushed without a
    // Turbo visit (anchors), and this guard runs before Turbo updates it.
    const leftIndex = this.turboHistory()?.currentIndex;
    if (index === undefined || leftIndex === undefined) return;

    if (!this.isDirty() || window.confirm(this.messageValue)) return;

    event.stopImmediatePropagation();
    this.ignoreNextPopState = true;
    history.go(leftIndex - index);
  };

  turboHistory(): { currentIndex?: number } | undefined {
    return (window as { Turbo?: { session?: { history?: { currentIndex?: number } } } }).Turbo?.session?.history;
  }
}
